/**
 * BrowseTab Component
 *
 * Browse all available layers organized by category or group.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt, faFolder } from '@fortawesome/free-solid-svg-icons';
import './BrowseTab.scss';
import { useLayerDrawer } from '../LayerDrawerContext';
import { LayerCard } from './LayerCard';
import { LayerGroupCard } from './LayerGroupCard';
import { LiveLayerCard } from './LiveLayerCard';
import { groupLayersByCategory, groupLayersByGroup, getCategoryDisplayName } from '../utils/layerDrawerUtils';
import { Layer } from '../../../types/layer';

type GroupByMode = 'category' | 'group';

export const BrowseTab: React.FC = () => {
  const { layers, loading, error, toggleLayer, liveLayers, toggleLiveLayer } = useLayerDrawer();
  const [groupByMode, setGroupByMode] = useState<GroupByMode>('group');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  /**
   * Group layers based on current mode
   */
  const groupedLayers = useMemo(() => {
    if (groupByMode === 'category') {
      return groupLayersByCategory(layers);
    } else {
      return groupLayersByGroup(layers);
    }
  }, [layers, groupByMode]);

  /**
   * Toggle group expansion
   */
  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  /**
   * Expand all groups
   */
  const expandAll = useCallback(() => {
    setExpandedGroups(new Set(Array.from(groupedLayers.keys())));
  }, [groupedLayers]);

  /**
   * Collapse all groups
   */
  const collapseAll = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  /**
   * Toggle all layers in a group (only for overlay groups)
   */
  const handleToggleGroup = useCallback(async (groupLayers: Layer[], enable: boolean) => {
    try {
      // Toggle each layer in the group
      for (const layer of groupLayers) {
        if (layer.enabled !== enable) {
          await toggleLayer(layer.id);
        }
      }
    } catch (error) {
      console.error('Failed to toggle group:', error);
    }
  }, [toggleLayer]);

  if (loading) {
    return (
      <div className="browse-tab">
        <div className="browse-tab-loading">
          <div className="loading-spinner"><FontAwesomeIcon icon={faSyncAlt} spin /></div>
          <div className="loading-text">Loading layers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="browse-tab">
        <div className="browse-tab-error">
          <div className="error-icon">⚠</div>
          <div className="error-text">{error}</div>
        </div>
      </div>
    );
  }

  if (layers.length === 0) {
    return (
      <div className="browse-tab">
        <div className="browse-tab-empty">
          <div className="empty-icon"><FontAwesomeIcon icon={faFolder} /></div>
          <div className="empty-text">No layers available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="browse-tab">
      <div className="browse-tab-header">
        <div className="browse-tab-title">Browse Layers</div>
        <div className="browse-tab-count">{layers.length} layer{layers.length !== 1 ? 's' : ''}</div>
      </div>

      <div className="browse-tab-controls">
        <div className="browse-tab-group-mode">
          <button
            className={`group-mode-btn ${groupByMode === 'category' ? 'active' : ''}`}
            onClick={() => setGroupByMode('category')}
            aria-pressed={groupByMode === 'category'}
          >
            By Category
          </button>
          <button
            className={`group-mode-btn ${groupByMode === 'group' ? 'active' : ''}`}
            onClick={() => setGroupByMode('group')}
            aria-pressed={groupByMode === 'group'}
          >
            By Group
          </button>
        </div>

        <div className="browse-tab-expand-controls">
          <button
            className="expand-control-btn"
            onClick={expandAll}
            aria-label="Expand all groups"
            title="Expand all"
          >
            Expand All
          </button>
          <button
            className="expand-control-btn"
            onClick={collapseAll}
            aria-label="Collapse all groups"
            title="Collapse all"
          >
            Collapse All
          </button>
        </div>
      </div>

      <div className="browse-tab-list">
        {/* Live Layers Section - Always show at top if any are registered */}
        {liveLayers.length > 0 && (
          <LayerGroupCard
            key="live-layers"
            title="Live Data"
            layers={[]}
            totalCount={liveLayers.length}
            enabledCount={liveLayers.filter(l => l.enabled).length}
            isExpanded={expandedGroups.has('live-layers')}
            onToggle={() => toggleGroup('live-layers')}
          >
            {liveLayers.map(liveLayer => (
              <LiveLayerCard
                key={liveLayer.id}
                liveLayer={liveLayer}
                onToggle={() => toggleLiveLayer(liveLayer.id)}
              />
            ))}
          </LayerGroupCard>
        )}

        {/* Database Layers */}
        {Array.from(groupedLayers.entries()).map(([groupKey, groupLayers]) => {
          const isExpanded = expandedGroups.has(groupKey);
          const displayName = groupByMode === 'category'
            ? getCategoryDisplayName(groupKey)
            : groupKey;

          // Only add group toggle for overlay groups
          const isOverlayGroup = groupLayers.every(layer => layer.category === 'overlays');

          return (
            <LayerGroupCard
              key={groupKey}
              title={displayName}
              layers={groupLayers}
              isExpanded={isExpanded}
              onToggle={() => toggleGroup(groupKey)}
              onToggleGroup={isOverlayGroup ? (enable) => handleToggleGroup(groupLayers, enable) : undefined}
            >
              {groupLayers.map(layer => (
                <LayerCard
                  key={layer.id}
                  layer={layer}
                  showOpacity={false}
                  showInfo={true}
                  showDragHandle={false}
                />
              ))}
            </LayerGroupCard>
          );
        })}
      </div>
    </div>
  );
};
